<template>
	<div :class="['ecos-shelf-progress', { 'ecos-shelf-progress--overlay': overlay, 'ecos-shelf-progress--done': done, 'ecos-shelf-progress--indeterminate': indeterminate }]">
		<div class="ecos-shelf-progress__card" role="progressbar" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="indeterminate ? undefined : percent" :aria-valuetext="valueText" :style="{ width: `${size}px` }">
			<svg class="ecos-shelf-progress__svg" :viewBox="viewBox" aria-hidden="true" focusable="false">
				<defs>
					<clipPath :id="boardClipId">
						<rect :x="SHELF.board.x" :y="SHELF.board.y" :width="SHELF.board.w" :height="SHELF.board.h" :rx="SHELF.board.h / 2" />
					</clipPath>
				</defs>
				<g v-if="decor.length">
					<path v-for="(item, k) in decor" :key="`decor-${k}`" :d="item.d" :fill="item.fill ?? 'none'" :stroke="item.stroke" :stroke-width="item.sw" stroke-linecap="round" :class="item.anim && `ecos-shelf-progress__decor--${item.anim}`" :style="item.i !== undefined ? { '--i': item.i } : undefined" />
				</g>
				<rect class="ecos-shelf-progress__track" :x="SHELF.board.x" :y="SHELF.board.y" :width="SHELF.board.w" :height="SHELF.board.h" :rx="SHELF.board.h / 2" />
				<g v-if="indeterminate" :clip-path="`url(#${boardClipId})`">
					<rect class="ecos-shelf-progress__runner" :x="SHELF.board.x" :y="SHELF.board.y" :width="SHELF.board.w * 0.3" :height="SHELF.board.h" :rx="SHELF.board.h / 2" />
				</g>
				<rect v-else class="ecos-shelf-progress__fill" :x="SHELF.board.x" :y="SHELF.board.y" :width="SHELF.board.w" :height="SHELF.board.h" :rx="SHELF.board.h / 2" :style="{ transform: `scaleX(${shown / 100})` }" />
				<g v-for="(shapes, i) in items" :key="`${themeId ?? 'neutral'}-${i}`">
					<g class="ecos-shelf-progress__ghost">
						<path v-for="([d], j) in shapes" :key="j" :d="d" />
					</g>
					<g :class="['ecos-shelf-progress__product', { 'ecos-shelf-progress__product--placed': isPlaced(i) }]">
						<path v-for="([d, fill], j) in shapes" :key="j" :d="d" :fill="fill" />
					</g>
				</g>
			</svg>
			<div class="ecos-shelf-progress__text">
				<div class="ecos-shelf-progress__line">
					<span class="ecos-shelf-progress__title">
						<svg v-if="done" class="ecos-shelf-progress__check" viewBox="0 0 24 24" aria-hidden="true">
							<circle cx="12" cy="12" r="11" />
							<path d="M7 12.5l3.2 3.2L17 9" />
						</svg>
						{{ done ? doneLabel : label }}
					</span>
					<span v-if="!done && !indeterminate" class="ecos-shelf-progress__percent">{{ percent }}&#8239;%</span>
				</div>
			</div>
			<div v-if="$slots.actions" class="ecos-shelf-progress__actions">
				<slot name="actions" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue';
import { SHELF } from '@/utils/ui/ecosShelf';
import { SHELF_THEMES, THEME_VIEWBOX } from '@/utils/ui/ecosShelfThemes';

const props = defineProps({
	value: { type: Number, default: null },
	label: { type: String, default: '' },
	doneLabel: { type: String, default: '' },
	doneDelay: { type: Number, default: 800 },
	size: { type: Number, default: 300 },
	overlay: { type: Boolean, default: false },
	theme: { type: String, default: '' },
});

const emit = defineEmits(['done']);

const boardClipId = `ecos-shelf-board-${useId().replace(/[^\w-]/g, '')}`;

const themeId = computed(() => (props.theme && Object.hasOwn(SHELF_THEMES, props.theme) ? props.theme : null));
const viewBox = computed(() => (themeId.value ? THEME_VIEWBOX : SHELF.viewBox));
const items = computed(() => SHELF.items.map((shapes, i) => SHELF_THEMES[themeId.value]?.replace[i] ?? shapes));
const decor = computed(() => SHELF_THEMES[themeId.value]?.decor ?? []);

const indeterminate = computed(() => props.value === null);
const shown = ref(0);
watch(
	() => props.value,
	(value) => {
		if (value === null) {
			shown.value = 0;
			return;
		}
		const clamped = Math.min(100, Math.max(0, Number(value) || 0));
		shown.value = clamped === 0 ? 0 : Math.max(shown.value, clamped);
	},
	{ immediate: true }
);

const done = computed(() => Boolean(props.doneLabel) && !indeterminate.value && shown.value >= 100);
const percent = computed(() => Math.floor(shown.value));
const isPlaced = (i) => indeterminate.value || shown.value >= ((i + 1) * 100) / items.value.length;

let doneTimer = null;
watch(
	done,
	(isDone) => {
		clearTimeout(doneTimer);
		if (isDone) doneTimer = setTimeout(() => emit('done'), props.doneDelay);
	},
	{ immediate: true }
);
onBeforeUnmount(() => clearTimeout(doneTimer));

const valueText = computed(() => {
	if (done.value) return props.doneLabel;
	return indeterminate.value ? props.label : `${props.label} ${percent.value} %`;
});
</script>
